import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import { TEACHER_SESSION_KEY } from "../../../Definition";
import { useDraggable } from "../../../hooks/useDraggable";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
import Button from "../../../atoms/Button";
import ChapterCard from "../../../molecules/ChapterCard";
import SelectAnswersModal from "../../../molecules/SelectAnswersModal";
import LessonCompleteConfirmModal from "../../../molecules/LessonCompleteConfirmModal";
import EvaluationLessonModal from "../EvaluationLessonModal";
import ModalThreeRules from "../ModalThreeRules";
import Timer from "../Timer";
import ModalAnswers from "../../../templates/teacher/ModalAnswers";
import ModalEditGroupClass from "../../../templates/teacher/ModalEditGroupClass";
import { ITeacherLessonRetrospectives, VideoChapter } from "../../../state";

import styles from "./VideoLessonSideMenu.module.scss";

interface VideoLessonSideMenuProps {
  lessonId: number;
  lessonName: string;
  lessonTitle: string;
  teachingMaterialId: number;
  teachingMaterialName: string;
  isDisplayAllSchoolsAnswer: boolean;
  videoChapters: VideoChapter[];
  playingChapterId: number;
  nextChapterId: number | null;
  classList: number[];
  prevLessonId: number | null;
  nextLessonId: number | null;
  isLessonCompleted: boolean;
  teacherLessonRetrospectives: ITeacherLessonRetrospectives;
  showChapterMenu: boolean;
  postCompletedLesson: () => void;
  postCompletedLessonRetrospectives: (
    score: number,
    lessonDeliveryScore: number,
    groupWorkScore: number | null,
  ) => void;
  prevLesson: () => void;
  nextLesson: () => void;
  setShowChapterMenu: (show: boolean) => void;
}

export const VideoLessonSideMenu = ({
  lessonId,
  lessonName,
  lessonTitle,
  teachingMaterialId,
  teachingMaterialName,
  isDisplayAllSchoolsAnswer,
  videoChapters,
  playingChapterId,
  nextChapterId,
  classList,
  prevLessonId,
  nextLessonId,
  isLessonCompleted,
  teacherLessonRetrospectives,
  showChapterMenu,
  postCompletedLesson,
  postCompletedLessonRetrospectives,
  prevLesson,
  nextLesson,
  setShowChapterMenu,
}: VideoLessonSideMenuProps) => {
  const history = useHistory();
  const extra = sessionStorage.getItem(TEACHER_SESSION_KEY)
    ? `&session_id=${sessionStorage.getItem(TEACHER_SESSION_KEY)}`
    : "";
  const issues = videoChapters.flatMap(chapter => chapter.issues);
  const retrospectiveWorkTitle = [...issues]
    .reverse()
    .find(issue => issue.title.match(/^ワーク\d+/))?.title;

  const [
    showLessonCompleteConfirmModal,
    setShowLessonCompleteConfirmModal,
  ] = useState(false);
  const [showEvaluationLessonModal, setShowEvaluationLessonModal] = useState(
    false,
  );
  const [showSelectAnswersModal, setShowSelectAnswersModal] = useState(false);
  const [issueId, setIssueId] = useState<number | null>(null);
  const [showThreeRules, setShowThreeRules] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [showModalEditGroupClass, setShowModalEditGroupClass] = useState(false);

  const videoChapterRef = useRef(null);
  useOnClickOutside(videoChapterRef, () => {
    !showLessonCompleteConfirmModal && setShowChapterMenu(false);
  });

  const answersButtonRef = useRef<HTMLButtonElement>(null);
  const [answersButtonRect, setAnswersButtonRect] = useState<DOMRect>();

  const playingButton = (id: number) => {
    setShowChapterMenu(false);
    history.push({
      search: `?lesson=${lessonId}&chapter=${id}&classList=${classList}`,
    });
  };

  const showAnswersByIssue = (issueId: number) => {
    setShowSelectAnswersModal(false);
    setIssueId(issueId);
  };

  const {
    position: timerPos,
    onMouseDown: onTimerDragStart,
    reset: resetTimerPos,
  } = useDraggable(0, 0);

  useEffect(() => {
    if (!showTimer) resetTimerPos();
  }, [showTimer, resetTimerPos]);

  useEffect(() => {
    const updateRect = () => {
      setAnswersButtonRect(answersButtonRef.current?.getBoundingClientRect());
    };

    updateRect();
    window.addEventListener("resize", updateRect);
    return () => window.removeEventListener("resize", updateRect);
  }, []);

  return (
    <div ref={videoChapterRef} className={styles.lessonSideMenuWrapper}>
      {showLessonCompleteConfirmModal && (
        <LessonCompleteConfirmModal
          title={"このクラスの授業は終了しましたか？"}
          buttons={[
            {
              title: "いいえ",
              action: () => {
                setShowLessonCompleteConfirmModal(false);
              },
              type: "None",
            },
            {
              title: "はい",
              action: () => {
                setShowLessonCompleteConfirmModal(false);
                setShowEvaluationLessonModal(true);
              },
              type: "Success",
            },
          ]}
          onClose={() => setShowLessonCompleteConfirmModal(false)}
        />
      )}
      {showEvaluationLessonModal && (
        <EvaluationLessonModal
          teacherLessonRetrospectives={teacherLessonRetrospectives}
          onRatingCompleted={(score, lessonDeliveryScore, groupWorkScore) => {
            postCompletedLesson();
            postCompletedLessonRetrospectives(
              score,
              lessonDeliveryScore,
              groupWorkScore,
            );
            setShowEvaluationLessonModal(false);
            window.location.href = `${process.env.REACT_APP_ONE_MENU_URL}/class_retrospect/${classList[0]}?teaching_material_id=${teachingMaterialId}&lesson_id=${lessonId}${extra}`;
          }}
          onClose={() => setShowEvaluationLessonModal(false)}
        />
      )}
      {showChapterMenu && (
        <div className={styles.chapterMenuWrapper}>
          <div className={styles.chapterMenu}>
            <div className={styles.chaptersContainer}>
              {videoChapters.map(chapter => (
                <ChapterCard
                  key={chapter.id}
                  chapter={chapter}
                  retrospectiveWorkTitle={retrospectiveWorkTitle}
                  isInitialOpen={chapter.id === nextChapterId}
                  isPlaying={chapter.id === playingChapterId}
                  handlePlayingButton={playingButton}
                />
              ))}
            </div>
            {!isLessonCompleted && (
              <Button
                className={styles.finishLessonButton}
                variant="normal"
                onClick={() => setShowLessonCompleteConfirmModal(true)}
                name="授業を終了する"
              />
            )}
          </div>
        </div>
      )}
      {showSelectAnswersModal && (
        <SelectAnswersModal
          parentPositionTop={
            answersButtonRect ? `${answersButtonRect?.top}px` : `0px`
          }
          parentPositionLeft={
            answersButtonRect ? `${answersButtonRect?.left}px` : `0px`
          }
          lessonName={lessonName}
          lessonTitle={lessonTitle}
          teachingMaterialName={teachingMaterialName}
          issues={issues}
          retrospectiveWorkTitle={retrospectiveWorkTitle}
          handleClose={() => setShowSelectAnswersModal(false)}
          handleClickIssue={showAnswersByIssue}
        />
      )}
      {issueId && (
        <ModalAnswers
          is_display_all_schools_answer={isDisplayAllSchoolsAnswer}
          issueID={issueId}
          classIDs={classList}
          onClose={() => {
            setIssueId(null);
          }}
        />
      )}
      {showThreeRules && (
        <ModalThreeRules
          onClose={() => {
            setShowThreeRules(false);
          }}
        />
      )}
      {showTimer && (
        <div
          className={styles.timer}
          onMouseDown={onTimerDragStart}
          style={{
            transform: `translate(${timerPos.x}px, ${timerPos.y}px)`,
            cursor: "move",
          }}
        >
          <Timer
            className={styles.timer__timerInShareScreen}
            onClose={() => setShowTimer(false)}
          />
        </div>
      )}
      {showModalEditGroupClass && (
        <ModalEditGroupClass
          classIDs={classList}
          onClose={() => setShowModalEditGroupClass(false)}
        />
      )}
      <div className={styles.lessonSideMenu}>
        <div className={styles.topButtons}>
          <button
            className={clsx(styles.actionBtn, styles.primary)}
            onClick={() => setShowChapterMenu(!showChapterMenu)}
          >
            動画チャプター
          </button>

          <button
            ref={answersButtonRef}
            className={clsx(
              styles.actionBtn,
              styles.primary,
              showSelectAnswersModal && styles.activeBtnInModal,
            )}
            onClick={() => {
              setShowSelectAnswersModal(!showSelectAnswersModal);
            }}
          >
            みんなの回答
          </button>
          <button
            className={styles.actionBtn}
            onClick={() => {
              setShowThreeRules(!showThreeRules);
            }}
          >
            3つのルール
          </button>
          <button
            className={clsx(styles.actionBtn, styles.mb16)}
            onClick={() => {
              setShowTimer(!showTimer);
            }}
          >
            タイマー
          </button>
          <button
            className={styles.actionBtn}
            onClick={() => {
              setShowModalEditGroupClass(!showModalEditGroupClass);
            }}
          >
            グループ設定
          </button>
        </div>
        <div className={styles.bottomButtons}>
          <div className={styles.navButtons}>
            <button
              className={styles.actionBtn}
              disabled={!prevLessonId}
              onClick={prevLesson}
            >
              前のLesson
            </button>
            <button
              className={styles.actionBtn}
              disabled={!nextLessonId}
              onClick={nextLesson}
            >
              次のLesson
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoLessonSideMenu;
